import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, TrophyIcon, ShoppingBagIcon, CoffeeIcon, TicketIcon, ArrowLeftIcon, HomeIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
interface RewardsScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
export function RewardsScreen({
  onBack,
  onNavigateHome
}: RewardsScreenProps = {}) {
  const {
    isDark
  } = useTheme();
  const rewards = [{
    icon: ShoppingBagIcon,
    title: 'PKR 500 Voucher',
    points: 2500,
    color: '#10B981'
  }, {
    icon: CoffeeIcon,
    title: 'Free Coffee',
    points: 1000,
    color: '#F37024'
  }, {
    icon: TicketIcon,
    title: 'Movie Tickets',
    points: 3500,
    color: '#8B5CF6'
  }, {
    icon: GiftIcon,
    title: 'Gift Card PKR 1000',
    points: 5000,
    color: '#EF4444'
  }];
  const recentActivity = [{
    action: 'Earned 150 points',
    detail: 'Bill payment',
    date: 'Today'
  }, {
    action: 'Redeemed Coffee Voucher',
    detail: '-1000 points',
    date: 'Yesterday'
  }, {
    action: 'Earned 500 points',
    detail: 'Salary credit',
    date: 'Dec 28'
  }];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className={`w-full min-h-screen pb-20 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2729">
      {/* Header */}
      <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2730">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/5 text-white' : 'hover:bg-black/5 text-[#303030]'}`} aria-label="Back" data-id="element-2731">
          <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-2732" />
        </button>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2733">
          Rewards
        </h1>
        <button onClick={onNavigateHome} className="w-10 h-10 rounded-full bg-[#F37024] flex items-center justify-center text-white hover:bg-[#D95A1A] transition-colors shadow-sm" aria-label="Home" data-id="element-2734">
          <HomeIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2735" />
        </button>
      </div>

      <div className="px-6 py-6 space-y-6" data-id="element-2736">
        {/* Points Balance */}
        <div className="bg-gradient-to-br from-[#F37024] to-[#D95A1A] rounded-2xl p-6 shadow-lg relative overflow-hidden" data-id="element-2737">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16" data-id="element-2738" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12" data-id="element-2739" />

          <div className="relative z-10 flex items-center gap-4" data-id="element-2740">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center" data-id="element-2741">
              <TrophyIcon className="w-8 h-8 text-white" strokeWidth={2} data-id="element-2742" />
            </div>
            <div data-id="element-2743">
              <p className="text-white text-opacity-90 text-sm mb-1" data-id="element-2744">
                Available Points
              </p>
              <h2 className="text-3xl font-bold text-white" data-id="element-2745">8,450</h2>
            </div>
          </div>
        </div>

        {/* Available Rewards */}
        <div data-id="element-2746">
          <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-2747">
            Redeem Rewards
          </h3>
          <div className="space-y-3" data-id="element-2748">
            {rewards.map((reward, index) => {
            const Icon = reward.icon;
            return <div key={index} className={`rounded-2xl p-4 flex items-center justify-between shadow-sm ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white'}`} data-id="element-2749">
                  <div className="flex items-center gap-4" data-id="element-2750">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: `${reward.color}15`
                }} data-id="element-2751">
                      <Icon className="w-6 h-6" style={{
                    color: reward.color
                  }} strokeWidth={2} data-id="element-2752" />
                    </div>
                    <div data-id="element-2753">
                      <h4 className={`text-[15px] font-bold mb-0.5 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2754">
                        {reward.title}
                      </h4>
                      <p className={`text-[13px] font-medium ${isDark ? 'text-[#F37024]' : 'text-[#F37024]'}`} data-id="element-2755">
                        {reward.points} pts
                      </p>
                    </div>
                  </div>
                  <button className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${isDark ? 'bg-[#3D2518] text-white hover:bg-[#4D3528]' : 'bg-[#F5EBE6] text-[#661806] hover:bg-[#E8D5CB]'}`} data-id="element-2756">
                    Redeem
                  </button>
                </div>;
          })}
          </div>
        </div>

        {/* Recent Activity */}
        <div data-id="element-2757">
          <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 mt-8 ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-2758">
            Recent Activity
          </h3>
          <div className={`rounded-2xl shadow-sm overflow-hidden ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white'}`} data-id="element-2759">
            {recentActivity.map((activity, index) => <div key={index} className={`p-4 flex items-center justify-between ${index !== recentActivity.length - 1 ? isDark ? 'border-b border-[#3D2518]' : 'border-b border-[#F5EBE6]' : ''}`} data-id="element-2760">
                <div data-id="element-2761">
                  <p className={`text-[14px] font-bold mb-0.5 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-2762">
                    {activity.action}
                  </p>
                  <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2763">
                    {activity.detail}
                  </p>
                </div>
                <span className={`text-[12px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-2764">
                  {activity.date}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}